import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Secret {
  id: string
  name: string
  type: string
  environment: string
  created_at: string
  expires_at: string | null
  last_rotated: string
  status: string
}

interface Vault {
  id: string
  name: string
  provider: string
  status: string
  secret_count: number
}

interface Statistics {
  total_secrets: number
  expiring_soon: number
  total_vaults: number
  last_rotation: string
  rotation_pending: number
}

export default function SecretsManagementPage() {
  const [secrets, setSecrets] = useState<Secret[]>([])
  const [vaults, setVaults] = useState<Vault[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'secrets' | 'vaults'>('secrets')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [secretsRes, vaultsRes, statsRes] = await Promise.all([
        fetch('/api/secrets/'),
        fetch('/api/secrets/vaults'),
        fetch('/api/secrets/statistics')
      ])

      if (secretsRes.ok) setSecrets(await secretsRes.json())
      if (vaultsRes.ok) setVaults(await vaultsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching secrets data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      expiring: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-red-100 text-red-800',
      rotated: 'bg-blue-100 text-blue-800'
    }
    return classes[status.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  const getTypeBadgeClass = (type: string) => {
    const classes: Record<string, string> = {
      api_key: 'bg-purple-100 text-purple-800',
      password: 'bg-red-100 text-red-800',
      certificate: 'bg-blue-100 text-blue-800',
      token: 'bg-green-100 text-green-800'
    }
    return classes[type.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Secrets Management</h1>
          <p className="mt-2 text-gray-600">Securely manage API keys, credentials, and certificates</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Secrets</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_secrets}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Expiring Soon</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.expiring_soon}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Vaults</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.total_vaults}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Last Rotation</p>
              <p className="text-lg font-bold text-green-600">{statistics.last_rotation}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Rotation Pending</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.rotation_pending}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('secrets')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'secrets'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Secrets ({secrets.length})
            </button>
            <button
              onClick={() => setActiveTab('vaults')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'vaults'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Vaults ({vaults.length})
            </button>
          </nav>
        </div>

        {/* Secrets Table */}
        {activeTab === 'secrets' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Environment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Rotated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {secrets.map((secret) => (
                  <tr key={secret.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">{secret.name}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeBadgeClass(secret.type)}`}>
                        {secret.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {secret.environment}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(secret.status)}`}>
                        {secret.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {secret.last_rotated}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {secret.expires_at || 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Vaults Grid */}
        {activeTab === 'vaults' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {vaults.map((vault) => (
              <div key={vault.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{vault.name}</h3>
                    <p className="text-sm text-gray-500">{vault.provider}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(vault.status)}`}>
                    {vault.status}
                  </span>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    <span className="font-medium text-gray-900">{vault.secret_count}</span> secrets stored
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
